import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import useSearch from '@hooks/useSearch';
import { getAllProducts } from '@services/dataService';
import Breadcrumbs from '@components/layout/Breadcrumbs';

/**
 * SearchResultsPage — Full search results with category grouping
 *
 * URL: /search?q=<query>
 * Shows: result count, grouped by category, competitor match badges,
 *        link to PDP, "no results" state with suggestions
 *
 * Source patterns: Catalog search results, ToolFinder /search route
 */

export default function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryParam = searchParams.get('q') || '';

  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [allProducts, setAllProducts] = useState([]);

  // Load products
  useEffect(() => {
    getAllProducts().then(setAllProducts).catch(() => setAllProducts([]));
  }, []);

  // Sync URL param to search query
  useEffect(() => {
    setSearchQuery(queryParam);
  }, [queryParam]);

  const { results, groupedResults, resultCount, isSearching, hasResults, highlightMatch } =
    useSearch(searchQuery, { products: allProducts, maxResults: 100 });

  const breadcrumbItems = [
    { label: 'Home', to: '/' },
    { label: `Search results for "${queryParam}"` },
  ];

  return (
    <div className="sandvik-container py-6">
      <Breadcrumbs items={breadcrumbItems} />

      {/* Header */}
      <div className="mb-6">
        <h1 className="font-sandvik-headline font-normal text-2xl md:text-3xl text-carbon-black mb-2">
          Search results
        </h1>
        {queryParam && (
          <p className="text-sm text-n5 font-sandvik-text">
            {isSearching ? (
              'Searching...'
            ) : (
              <>
                {resultCount} result{resultCount !== 1 ? 's' : ''} for{' '}
                <span className="font-normal text-carbon-black">"{queryParam}"</span>
              </>
            )}
          </p>
        )}
      </div>

      {/* Loading */}
      {isSearching && (
        <div className="flex items-center gap-3 py-12 justify-center">
          <span className="material-symbols-outlined text-2xl text-electric-blue animate-spin">progress_activity</span>
          <span className="text-sm text-n5 font-sandvik-text">Searching products...</span>
        </div>
      )}

      {/* No results */}
      {!isSearching && queryParam && !hasResults && (
        <div className="text-center py-16">
          <span className="material-symbols-outlined text-5xl text-n3 mb-4 block">search_off</span>
          <h2 className="text-lg font-normal font-sandvik-text text-carbon-black mb-2">No results found</h2>
          <p className="text-sm text-n5 font-sandvik-text max-w-md mx-auto mb-6">
            We couldn't find any products matching "{queryParam}". Try a different search term, item code, or competitor part number.
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-sm text-electric-blue hover:underline font-sandvik-text"
          >
            <span className="material-symbols-outlined text-sm">inventory_2</span>
            Browse all products
          </Link>
        </div>
      )}

      {/* Grouped results */}
      {!isSearching && hasResults && (
        <div className="space-y-8">
          {groupedResults.map((group) => (
            <section key={group.categoryId}>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-n2">
                <h2 className="text-sm font-normal font-sandvik-text text-carbon-black tracking-wide">
                  {group.categoryName}
                </h2>
                <span className="text-xs text-n4 font-sandvik-text">
                  ({group.products.length})
                </span>
                {group.productLine && group.productLine !== 'top-hammer' && (
                  <span className="text-[10px] font-normal px-2 py-0.5 bg-n1 text-n5 font-sandvik-text">
                    {group.productLine === 'dth' ? 'DTH' : group.productLine}
                  </span>
                )}
              </div>

              <div className="divide-y divide-n2">
                {group.products.map((product) => (
                  <Link
                    key={product.itemCode}
                    to={`/product/${product.itemCode}`}
                    className="flex items-center gap-4 py-3 px-2 hover:bg-n1 transition-colors group"
                  >
                    {/* Product thumbnail placeholder */}
                    <div className="w-14 h-14 bg-n1 flex-shrink-0 flex items-center justify-center">
                      <span className="material-symbols-outlined text-2xl text-n3">precision_manufacturing</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Competitor match badge */}
                      {product._competitorMatch && (
                        <span className="inline-flex items-center text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 mb-1 font-sandvik-text">
                          <span className="material-symbols-outlined text-xs mr-1">compare_arrows</span>
                          {product._competitorMatch.manufacturer}: {product._competitorMatch.partNumber}
                        </span>
                      )}
                      <p className="text-sm text-carbon-black font-sandvik-text truncate group-hover:text-electric-blue transition-colors">
                        {highlightMatch(product.name || product.description || product.itemCode, queryParam)}
                      </p>
                      <p className="text-xs text-n5 font-sandvik-text">
                        {product.itemCode}
                        {product.thread && ` · ${product.thread}`}
                        {product.diameterMm && ` · Ø${product.diameterMm}mm`}
                      </p>
                    </div>

                    <span className="material-symbols-outlined text-sm text-n4 group-hover:text-electric-blue transition-colors flex-shrink-0">
                      chevron_right
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* No query entered */}
      {!queryParam && (
        <div className="text-center py-16">
          <span className="material-symbols-outlined text-5xl text-n3 mb-4 block">search</span>
          <h2 className="text-lg font-normal font-sandvik-text text-carbon-black mb-2">Search for products</h2>
          <p className="text-sm text-n5 font-sandvik-text max-w-md mx-auto">
            Enter a product name, item code, thread type, or competitor part number to find what you need.
          </p>
        </div>
      )}
    </div>
  );
}
